import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type DrawDefinition, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import NiceModal from '@ebay/nice-modal-react';
import Grid from '@mui/material/Grid';
import { type Column, type Row } from '@tanstack/react-table';
import { deepEqual } from 'fast-equals';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import lazyWithPreload from 'react-lazy-with-preload';
import { useParams } from 'react-router-dom';
import { drawDefinitionConstants, tournamentEngine } from 'tods-competition-factory';

import { drawGeneratedAtomFamily, eventAtomFamily } from 'atoms/events';
import { tournamentAtom } from 'atoms/tournament';
import CompetitorsTableEmptyState from 'components/tables/competitors/CompetitorsTableEmptyState';
import CompetitorsTableTitle from 'components/tables/competitors/CompetitorsTableTitle';
import {
  availablePlayersTeamRequiredColumns,
  selectParticipantRequiredColumns,
  selectTeamRequiredColumns,
} from 'components/tables/competitors/config';
import { useCompetitorsTableColumns } from 'components/tables/competitors/useCompetitorsTableColumns';
import { type RowConfigInterface } from 'components/tables/dndStandardTable/types';
import DrawParticipantsTable from 'components/tables/eventsSplitDraws/drawParticipantsTable/DrawParticipantsTable';
import AddToDrawDialog from 'components/tables/eventsSplitDraws/moveEntries/AddToDrawDialog';
import RemoveFromDrawDialog from 'components/tables/eventsSplitDraws/moveEntries/RemoveFromDrawDialog';
import { BodyLargeLight, BodyXLarge } from 'components/typography/Typography';
import { Feature, featureEnabled } from 'config/clients';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { useCompetitorsPopover } from 'containers/eventsSplitDraws/entries/EntriesActions';
import RankingsAndRatingsContainer from 'containers/rankingsAndRatings/RankingsAndRatingsContainer';
import SeedingButton from 'containers/seeding/SeedingButton';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import useSeeds from 'hooks/tournament/useSeeds';
import { useLevelConfig } from 'hooks/useLevelConfiguration';
import useShallowState from 'hooks/useShallowState';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { type ScalingType } from 'utils/scalings';

import SelectParticipantsBulkActions from './SelectParticipantsBulkActions';
import { useStyles } from './styles';

const { MAIN, QUALIFYING } = drawDefinitionConstants;

const SetPlayerStatusDialog = lazyWithPreload(() => import('./SetPlayerStatusDialog'));

interface SelectParticipantsTableProps {
  alternateDrawParticipants?: ParticipantInterface[];
  drawDefinition?: DrawDefinition;
  entries: ParticipantInterface[];
  isQualifyingEnabled: boolean;
  qualifyingParticipants?: ParticipantInterface[];
  title?: string;
  onUnpairPlayers?: (rowItemId: string, participants: ParticipantInterface[]) => void;
  columnsSelection: Array<string>;
  saveColumnsSelection: (columns: Column<ParticipantInterface>[]) => void;
}

const SelectParticipantsTable: React.FC<React.PropsWithChildren<SelectParticipantsTableProps>> = ({
  alternateDrawParticipants,
  drawDefinition,
  entries,
  isQualifyingEnabled,
  qualifyingParticipants,
  title,
  onUnpairPlayers,
  columnsSelection,
  saveColumnsSelection,
}) => {
  useEffect(() => {
    SetPlayerStatusDialog.preload();
  }, []);
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { eventId } = useParams<{ eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const drawGenerated = useAtomValue(drawGeneratedAtomFamily(eventId));
  const openCompetitorsPopover = useCompetitorsPopover();
  const showProfileDialog = usePlayersProfileDialog();

  const addToDrawProgress = useProgress('addToDraw');
  const removeFromDrawProgress = useProgress('removeFromDraw');

  const [addToDraw] = useMutateTournament('addToDraw');
  const [removeFromDraw] = useMutateTournament('removeFromDraw');
  const [setPlayerStatus] = useMutateTournament('setPlayerStatus');

  const availableParticipantsCount =
    (entries?.length || 0) + (alternateDrawParticipants?.length || 0) + (qualifyingParticipants?.length || 0);
  const isDouble = event?.eventType === EventTypeEnum.DOUBLES;
  const isTeam = event?.eventType === EventTypeEnum.TEAM;
  const { rankingsEnabled } = useLevelConfig();
  const scalesToHide = useMemo(() => (rankingsEnabled ? [] : ['Ranking' as ScalingType]), [rankingsEnabled]);

  const isQualifyingDrawOnly = useMemo(
    () =>
      drawDefinition?.structures?.find((s) => s.stage === MAIN)?.matchUps?.length === 0 &&
      drawDefinition?.structures?.find((s) => s.stage === QUALIFYING)?.matchUps?.length > 0,
    [drawDefinition?.structures],
  );

  const drawId = drawDefinition?.drawId;
  const structure =
    drawDefinition?.structures?.length && drawDefinition.structures?.find((structure) => structure?.stage === 'MAIN');
  const getPositionAssignments = useCallback(
    () => tournamentEngine.getPositionAssignments({ drawId, structureId: structure?.structureId })?.positionAssignments,
    [drawId, structure],
  );
  const positionAssignments = useAtomValue(selectAtom(tournamentAtom, getPositionAssignments, deepEqual));
  const drawSize = !isQualifyingDrawOnly && positionAssignments?.length;
  const getSeedAssignments = useCallback(
    () => tournamentEngine.getStructureSeedAssignments({ structure })?.seedAssignments,
    [structure],
  );

  const getFlights = useCallback(
    () =>
      tournamentEngine.getFlightProfile({
        eventId: event.eventId,
      })?.flightProfile?.flights,
    [event],
  );

  const flights = useAtomValue(selectAtom(tournamentAtom, getFlights, deepEqual));

  const flightsExist = flights?.length > 0;
  const isSplitDraw = flights?.length > 1;

  const seedAssignments = useAtomValue(selectAtom(tournamentAtom, getSeedAssignments, deepEqual));
  const {
    inputRefs: seedRefs,
    isLoading,
    isSeedingPublished,
    seeds,
    clear: clearSeeds,
    publish: publishSeeds,
    save: saveSeeds,
    unpublish: unpublishSeeds,
    update: updateSeed,
  } = useSeeds({
    event,
    entries,
    seedAssignments,
    isGetSeeds: !isSplitDraw,
  });

  const isTeamPlayersTable = useMemo(
    () => isTeam && entries.every((entry) => entry.participantType === ParticipantTypeEnum.INDIVIDUAL),
    [isTeam, entries],
  );
  const isTeamTable = useMemo(
    () => isTeam && entries.some((entry) => entry.participantType === ParticipantTypeEnum.TEAM),
    [isTeam, entries],
  );

  const isSelectable = !isTeam || (isTeam && ((!drawGenerated && isTeamTable) || isTeamPlayersTable));

  const [isInEditMode, setIsInEditMode] = useState<boolean>(false);
  const [addToDrawDialogOpen, setAddToDrawDialogOpen] = useState<boolean>(false);
  const [removeToDrawDialogOpen, setRemoveToDrawDialogOpen] = useState<boolean>(false);
  const [selectedEntriesIds, setSelectedEntriesIds] = useShallowState<string[]>([]);

  const handleUnpairPlayers = useCallback(
    (participantId: string) => onUnpairPlayers(participantId, entries),
    [entries, onUnpairPlayers],
  );

  const rowConfig: RowConfigInterface = useMemo(
    () => ({
      isActive: (item: any) => !!selectedEntriesIds[item.id],
    }),
    [selectedEntriesIds],
  );

  const handleSetPlayerStatusSave = useCallback(
    async (playerStatus: string, entriesIds: string[]) => {
      await setPlayerStatus?.(entriesIds, event.eventId, playerStatus);
      setSelectedEntriesIds([]);
    },
    [setSelectedEntriesIds, event.eventId, setPlayerStatus],
  );

  const handleOpenSetPlayerStatusDialog = useCallback(
    async (participantIds: string[], resetRowSelection?: () => void) => {
      setSelectedEntriesIds(participantIds);
      await NiceModal.show(SetPlayerStatusDialog, {
        selectedEntriesIds: participantIds,
        onSave: (playerStatus: string, selectedEntriesIds: string[]) =>
          handleSetPlayerStatusSave(playerStatus, selectedEntriesIds),
        onResetRowSelection: resetRowSelection,
      });
    },
    [handleSetPlayerStatusSave, setSelectedEntriesIds],
  );

  const handleAddToDraw = useCallback(
    (participantIds: string[]) => {
      setSelectedEntriesIds(participantIds);
      setAddToDrawDialogOpen(true);
    },
    [setSelectedEntriesIds],
  );

  const handleAddToDrawDialogClose = useCallback(() => {
    setAddToDrawDialogOpen(false);
    setSelectedEntriesIds([]);
  }, [setSelectedEntriesIds]);

  const handleAddToDrawSave = useCallback(
    async (selectedDraws) => {
      await addToDraw?.(selectedDraws, selectedEntriesIds, event.eventId);
      setAddToDrawDialogOpen(false);
      setSelectedEntriesIds([]);
    },
    [addToDraw, setSelectedEntriesIds, event.eventId, selectedEntriesIds],
  );

  const handleRemoveFromDraw = useCallback(
    (participantIds: string[]) => {
      setSelectedEntriesIds(participantIds);
      setRemoveToDrawDialogOpen(true);
    },
    [setSelectedEntriesIds],
  );

  const handleRemoveFromDrawDialogClose = useCallback(() => {
    setRemoveToDrawDialogOpen(false);
    setSelectedEntriesIds([]);
  }, [setSelectedEntriesIds]);

  const handleRemoveFromDrawSave = useCallback(
    async (selectedDraws) => {
      await removeFromDraw?.(selectedDraws, selectedEntriesIds, event.eventId);
      setRemoveToDrawDialogOpen(false);
      setSelectedEntriesIds([]);
    },
    [setSelectedEntriesIds, event.eventId, removeFromDraw, selectedEntriesIds],
  );

  const handleChangeIsEditMode = useCallback((editMode: boolean) => setIsInEditMode(editMode), []);

  const renderSelectParticipantsBulkActions = useCallback(
    (selectedFlatRows: Row<ParticipantInterface>[], resetRowSelection: () => void) => (
      <SelectParticipantsBulkActions
        flightsExist={flightsExist}
        isQualifyingEnabled={isQualifyingEnabled}
        selectedFlatRows={selectedFlatRows}
        onClickAddToDraw={handleAddToDraw}
        onClickRemoveFromDraw={handleRemoveFromDraw}
        onClickSetPlayerStatus={handleOpenSetPlayerStatusDialog}
        onResetRowSelection={resetRowSelection}
      />
    ),
    [flightsExist, handleAddToDraw, handleOpenSetPlayerStatusDialog, handleRemoveFromDraw, isQualifyingEnabled],
  );

  const handleOpenCompetitorsPopover = useCallback(
    (participant: ParticipantInterface, clickEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      openCompetitorsPopover(
        participant,
        {
          onAddToDraw: handleAddToDraw,
          onRemoveFromDraw: handleRemoveFromDraw,
          onUnpairPlayers: handleUnpairPlayers,
        },
        clickEvent.currentTarget,
        event,
        isQualifyingEnabled,
        flightsExist,
        isTeamPlayersTable,
      ),
    [
      flightsExist,
      event,
      handleAddToDraw,
      handleRemoveFromDraw,
      handleUnpairPlayers,
      isQualifyingEnabled,
      isTeamPlayersTable,
      openCompetitorsPopover,
    ],
  );

  const entriesTableColumns = useCompetitorsTableColumns({
    classes,
    event,
    flights,
    isInEditMode,
    isTeamTable: isTeamTable,
    onChangeSeeding: updateSeed,
    onOpenPlayerProfile: showProfileDialog,
    onClickAction: handleOpenCompetitorsPopover,
    requiredColumns: isTeamTable
      ? selectTeamRequiredColumns
      : isTeamPlayersTable
        ? availablePlayersTeamRequiredColumns
        : selectParticipantRequiredColumns,
    seeds,
    manualSeedRefs: seedRefs,
    isSelectedTable: true,
  });

  const additionalButtons = useMemo(
    () =>
      !isTeamPlayersTable && (
        <SeedingButton
          entries={entries}
          isInEditMode={isInEditMode}
          isSeedingPublished={isSeedingPublished}
          loading={isLoading}
          seedingActions={{
            onClearSeeds: clearSeeds,
            onPublishSeeds: publishSeeds,
            onSaveSeeds: saveSeeds,
            onUnpublishSeeds: unpublishSeeds,
          }}
          onSetIsInEditMode={handleChangeIsEditMode}
        />
      ),
    [
      clearSeeds,
      entries,
      handleChangeIsEditMode,
      isInEditMode,
      isLoading,
      isSeedingPublished,
      isTeamPlayersTable,
      publishSeeds,
      saveSeeds,
      unpublishSeeds,
    ],
  );

  return useMemo(
    () => (
      <>
        <Grid container direction="column" spacing={2}>
          {!!title && (
            <Grid container item>
              <CompetitorsTableTitle
                length={`${entries.length} / ${availableParticipantsCount || drawSize || 0}`}
                text={title ?? t('selectedParticipants')}
              />
            </Grid>
          )}
          {entries?.length > 0 ? (
            <>
              {featureEnabled(Feature.RANKINGS_AND_RATINGS) && (
                <Grid container item>
                  <RankingsAndRatingsContainer event={event} scalesToHide={scalesToHide} />
                </Grid>
              )}
              <Grid container item>
                <DrawParticipantsTable
                  additionalButtons={additionalButtons}
                  columns={entriesTableColumns}
                  data={entries}
                  isDouble={isDouble}
                  isEnableMultiRowSelection={isSelectable}
                  rowConfig={rowConfig}
                  saveColumnsSelection={saveColumnsSelection}
                  savedColumns={columnsSelection}
                  selectedActions={renderSelectParticipantsBulkActions}
                  type="SELECTED"
                />
              </Grid>
            </>
          ) : (
            <CompetitorsTableEmptyState>
              <BodyXLarge className={classes.noParticipants}>{t('noParticipants')}</BodyXLarge>
              <BodyLargeLight>{t('noParticipantsMessage')}</BodyLargeLight>
            </CompetitorsTableEmptyState>
          )}
        </Grid>
        <AddToDrawDialog
          flights={flights}
          loading={addToDrawProgress}
          open={addToDrawDialogOpen || addToDrawProgress}
          selectedEntriesIds={selectedEntriesIds}
          onClose={handleAddToDrawDialogClose}
          onSave={handleAddToDrawSave}
        />
        <RemoveFromDrawDialog
          flights={flights}
          loading={removeFromDrawProgress}
          open={removeToDrawDialogOpen || removeFromDrawProgress}
          selectedEntriesIds={selectedEntriesIds}
          onClose={handleRemoveFromDrawDialogClose}
          onSave={handleRemoveFromDrawSave}
        />
      </>
    ),
    [
      title,
      entries,
      availableParticipantsCount,
      drawSize,
      t,
      scalesToHide,
      event,
      additionalButtons,
      entriesTableColumns,
      isDouble,
      rowConfig,
      isSelectable,
      renderSelectParticipantsBulkActions,
      classes.noParticipants,
      addToDrawDialogOpen,
      addToDrawProgress,
      flights,
      selectedEntriesIds,
      handleAddToDrawDialogClose,
      handleAddToDrawSave,
      removeToDrawDialogOpen,
      removeFromDrawProgress,
      handleRemoveFromDrawDialogClose,
      handleRemoveFromDrawSave,
      columnsSelection,
      saveColumnsSelection,
    ],
  );
};

export default SelectParticipantsTable;
